// Routes.tsx
import React, { lazy, Suspense } from "react";
import { Navigate, useRoutes } from "react-router-dom";
import Permissions from "src/common_constants/Permission";
import CustomerOnboardingLayout from "src/components/index/CustomerOnboardingLayout";
import FreeTrailLayout from "src/components/index/FreeTrailLayout";
import { Layout as DashboardLayout } from "src/components/index/Layout";

// import {SourceGrid} from "src/components/settings/source";
import { usePermission } from "src/context/PermissionContext";
import AuthGuard from "src/guards/AuthGuard";
import GuestGuard from "src/guards/GuestGuard";
import Authentication from "src/pages/Authentication";
import CustomLoader from "../shared_components/CustomLoader";
import ProtectedRoute from "./protectedRoute";
import { TaxMappingPage } from "src/components/settings/ClientSettings/Tax";

interface RoutesProps { }
const Dashboard = lazy(() => import("src/pages/Dashboard"));
const Reservation = lazy(() => import("src/pages/Reservation"));
const VehicleExchangeForm = lazy(
  () =>
    import(
      "src/components/booking/reservation/view/tabs/vehicleExchange/vehicleExchangeForm"
    )
);
const Payments = lazy(
  () =>
    import(
      "src/components/booking/reservation/view/tabs/payments/Payments"
    )
);
const Contract = lazy(() => import("src/pages/Contract"));
const SourceGrid = lazy(
  () => import("src/components/settings/source/SourceGrid")
);
const CallAgent = lazy(() => import("src/pages/CallAgent"));
const EmailLoader = lazy(() => import("src/pages/EmailLoader"));
const ContractSigned = lazy(
  () => import("src/components/booking/contract/ContractSigned")
);

const Asset = lazy(() => import("src/pages/Asset"));
const VehicleMaintenanceScheduleGrid = lazy(
  () =>
    import(
      "src/components/settings/MaintenanceSchedules/VehicleMaintenanceScheduleGrid"
    )
);
const Customer = lazy(() => import("src/pages/Customer"));

const Calendar = lazy(() => import("src/pages/Calendar"));

const Settings = lazy(() => import("src/pages/Settings"));

const AddLocation = lazy(
  () => import("src/components/CustomerOnbording/AddLocation")
);
const WelcomeScreen = lazy(
  () => import("src/components/CustomerOnbording/welcomeScreen")
);
const AddressDetailsPage = lazy(
  () => import("src/components/FreeTrail/AddClientPage")
);
const FinalSubmissionPage = lazy(
  () => import("src/components/FreeTrail/FinalSubmissionPage")
);
const PaymentFormPage = lazy(
  () => import("src/components/FreeTrail/PaymentFormPage")
);
const WelcomePage = lazy(
  () => import("src/components/FreeTrail/WelcomePage")
);
const ProfilePhotoUploader = lazy(
  () => import("src/components/CustomerOnbording/ProfilePhotoUploader")
);
const AddCodeSequence = lazy(
  () => import("src/components/CustomerOnbording/AddCodeSequence")
);
const AddRateInfo = lazy(
  () => import("src/components/CustomerOnbording/AddRateInfo")
);
const AddVehicleType = lazy(
  () => import("src/components/CustomerOnbording/AddVehicleType")
);

//Start user management
const Profile = lazy(
  () => import("src/components/settings/user-management/Profile")
);
const UserManagementGrid = lazy(
  () => import("src/components/settings/user-management/UserManagementGrid")
);
const UserView = lazy(
  () => import("src/components/settings/user-management/UserView")
);
const ChangePassword = lazy(
  () => import("src/components/settings/ClientSettings/Change-Password")
);
const Store = lazy(() => import("src/components/settings/store/index"));
//end user management

const SettingsLocationManagement = lazy(
  () => import("src/components/settings/location-management/index")
);

const SettingsDriverManagement = lazy(
  () => import("src/components/settings/DriverManagement/index")
);

const SettingVehicleModelManagement = lazy(
  () => import("src/components/settings/Asset-management-Model/index")
);

const SettingVehicleTypeManagement = lazy(
  () => import("src/components/settings/Asset-management-Type/index")
);

const SettingVehicleOptionManagement = lazy(
  () => import("src/components/settings/Asset-management-Option/index")
);

const SettingsInsuranceManagement = lazy(
  () => import("src/components/settings/insurance-management/index")
);

const SettingsVechicleMakeManagement = lazy(
  () => import("src/components/settings/Asset-management-Make/index")
);

//Start asset rate name management
const RateNameManagementGrid = lazy(
  () =>
    import(
      "src/components/settings/rate-name-management/RateNameManagementGrid"
    )
);

const DamagesForm = lazy(
  () => import("src/components/damageAI/ContractDamageView")
);
const AddDamagesForm = lazy(
  () => import("src/components/damageAI/DamageCheckoutCheckinView")
);
const DetectDamagesForm = lazy(
  () => import("src/components/damageAI/CarDamageView")
);

const RatePlanManagementGrid = lazy(
  () =>
    import(
      "src/components/settings/rate-plan-management/RatePlanManagementGrid"
    )
);

const RatePlanView = lazy(
  () => import("src/components/settings/rate-plan-management/RatePlanView")
);

const AddOnView = lazy(
  () => import("src/components/settings/add-on-charges/AddOnCharges")
);

const PromotionView = lazy(
  () =>
    import(
      "src/components/settings/promotion-management/PromotionManagementGrid"
    )
);

const FuelPriceGrid = lazy(
  () => import("src/components/settings/fuelPrice/FuelPriceGrid")
);

const AddOnChargesPage
  = lazy(() => import("src/components/settings/add-on-charges/index"));

//end add on charges

//start client settings
const ClientView = lazy(
  () => import("src/components/settings/ClientSettings/Client-Profile/Profile")
);
const CilentEditView = lazy(
  () =>
    import(
      "src/components/settings/ClientSettings/Client-Profile/ClientEditForm"
    )
);

const TaxManagementGrid = lazy(
  () => import("src/components/settings/ClientSettings/Tax/TaxManagementGrid")
);

const AddOnChargesGrid = lazy(
  () => import("src/components/settings/add-on-charges/AddOnChargesGrid")
);
const ExpenseGrid = lazy(
  () => import("src/components/settings/expense/ExpenseGrid")
);
const OffenseTypeGrid = lazy(
  () => import("src/components/settings/offenseType/offenseTypeGrid")
);
const ReferralGrid = lazy(
  () => import("src/components/settings/referrals/referralGrid")
);
const TermsAndConditionGrid = lazy(
  () => import("src/components/settings/termsAndCondition/TermsAnddConditionGrid")
);
const VendorGrid = lazy(
  () => import("src/components/settings/vendor/VendorGrid")
);
//end client settings

//start Reports
const ReportsPage = lazy(() => import("src/pages/Reports"));
const Report = lazy(() => import("src/components/reports/components/Report"));
//end Reports

//start planner
const PlannerComponent = lazy(() => import("src/pages/Planner"));
//end planner
// start Invoices
const Invoice = lazy(() => import("src/pages/Invoice"));
const InvoiceForm = lazy(() => import("src/pages/Invoice"));
// end Invoices

const Routes: React.FC<RoutesProps> = () => {
  //Start Customer
  const canViewCustomer = usePermission(Permissions.CustomerView);
  const canCreateCustomer = usePermission(Permissions.CustomerCreate);
  const canUpdateCustomer = usePermission(Permissions.CustomerUpdate);
  //End Customer

  //Start Location
  const canViewLocation = usePermission(Permissions.LocationView);
  //End Location

  //Start Driver
  const canViewDriver = usePermission(Permissions.CustomerView);
  //End Driver

  //Start Insurance
  const canViewInsurance = usePermission(Permissions.InsuranceProviderView);

  //End Insurance

  //Start User
  const canViewProfile = usePermission(Permissions.UserViewSelf);
  const canViewUsersGrid = usePermission(Permissions.UserView);
  const canUpdateUserSelf = usePermission(Permissions.UserUpdateSelf);

  //End USer

  //Start Assest
  const canViewAsset = usePermission(Permissions.AssetView);
  const canCreateAsset = usePermission(Permissions.AssetCreate);
  const canUpdateAsset = usePermission(Permissions.AssetUpdate);
  const canViewAssestMake = usePermission(Permissions.AssetMakeView);
  const canViewAssestModel = usePermission(Permissions.AssetModelView);
  const canViewAssestType = usePermission(Permissions.AssetTypeView);
  const canViewAssestOption = usePermission(Permissions.AssetOptionView);

  //End Assest

  //start rate management
  const canViewAssetRate = usePermission(Permissions.AssetRateCreateModify);

  //end rate management

  //start client settings
  const canViewTax = usePermission(Permissions.AssetRateCreateModify);
  const canViewClientProfile = usePermission(Permissions.AssetRateCreateModify);
  const canCreateAndModifylientProfile = usePermission(
    Permissions.AssetRateCreateModify
  );

  const canViewReservation = usePermission(Permissions.BookingView);
  const canCreateModifyReservation = usePermission(
    Permissions.BookingCreateModify
  );

  const canCreateOrModifyContract = usePermission(
    Permissions.ContractCreateModify
  );
  const canViewContract = usePermission(Permissions.ContractView);

  //planner
  const canViewPlanner = usePermission(Permissions.BookingView);
  //end planner
  //Start Invoice
  const canViewInvoice = usePermission(Permissions.InvoiceView);
  const canCreateInvoice = usePermission(Permissions.InvoiceCreateModify);

  //Start Expense
  const canViewExpense = usePermission(Permissions.ExpenseView);
  const canCreateExpense = usePermission(Permissions.ExpenseCreateModify);
  //End Expesne

  //Start Vendor
  const canViewVendor = usePermission(Permissions.VendorView);
  const canCreateVendor = usePermission(Permissions.VendorCreateModify);
  //End Expesne

  //Start Terms
  const canViewTerms = usePermission(Permissions.TermsAndConditionView);
  const canCreateTerms = usePermission(Permissions.TermsAndConditionCreateModify);
  //End erms

  let routes = useRoutes([
    {
      path: "FreeTrial",
      element: <FreeTrailLayout />

      ,
      children: [
        {
          path: "",
          element: <Navigate to="CreateNewClient" replace />,
        },
        {
          path: "CreateNewClient",
          element: (
            // <Suspense fallback={<div>Loading...</div>}>
            <AddressDetailsPage />
            // </Suspense>
          ),
        },
        {
          path: "PaymentPage",
          element: (
            // <Suspense fallback={<div>Loading...</div>}>
            <PaymentFormPage />
            // </Suspense>
          ),
        },
        {
          path: "FinalSubmit",
          element: (
            // <Suspense fallback={<div>Loading...</div>}>
            <FinalSubmissionPage />
            // </Suspense>
          ),
        },
        {
          path: "Welcome",
          element: (
            // <Suspense fallback={<div>Loading...</div>}>
            <WelcomePage />
            // </Suspense>
          ),
        },
      ],
    },
    {
      path: "onbording",
      element: (
        <AuthGuard>
          <CustomerOnboardingLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "",
          element: <Navigate to="welcome" replace />,
        },
        {
          path: "welcome",
          element: (
            // <Suspense fallback={<div>Loading...</div>}>
            <WelcomeScreen />
            // </Suspense>
          ),
        },
        {
          path: "add-location",
          element: (
            // <Suspense fallback={<div>Loading...</div>}>
            <AddLocation />
            // </Suspense>
          ),
        },
        {
          path: "add-logo",
          element: (
            // <Suspense fallback={<div>Loading...</div>}>
            <ProfilePhotoUploader />
            // </Suspense>
          ),
        },
        {
          path: "add-codeSequence",
          element: (
            // <Suspense fallback={<div>Loading...</div>}>
            <AddCodeSequence />
            // </Suspense>
          ),
        },
        {
          path: "add-rateInfo",
          element: (
            // <Suspense fallback={<div>Loading...</div>}>
            <AddRateInfo />
            // </Suspense>
          ),
        },
        {
          path: "add-assetType",
          element: (
            // <Suspense fallback={<div>Loading...</div>}>
            <AddVehicleType />
            // </Suspense>
          ),
        },
      ],
    },
    {
      path: "/",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "Dashboard",
          element: <Dashboard />,
        },

        {
          path: "/",
          element: <Navigate to="/Dashboard" replace />,
        },

        { path: "Calendar", element: <Calendar /> },

        {
          path: "Reservation",
          children: [
            {
              path: "",
              element: (
                <ProtectedRoute canAccess={canViewReservation}>
                  <Reservation />
                </ProtectedRoute>
              ),
            },
            {
              path: "Add-Reservation",
              element: canCreateModifyReservation ? (
                <Reservation route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Edit-Reservation",
              element: canCreateModifyReservation ? (
                <Reservation route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "View-Reservation",
              element: canViewReservation ? (
                <Reservation route="view" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
              {
              path: "Payments",
              element: (
                <ProtectedRoute canAccess={canCreateModifyReservation}>
                  <Payments />
                </ProtectedRoute>
              ),
            },
          ],
        },

        {
          path: "Contract",
          children: [
            {
              path: "",
              element: (
                <ProtectedRoute canAccess={canViewContract}>
                  <Contract />
                </ProtectedRoute>
              ),
            },
            {
              path: "Add-Contract",
              element: (
                <ProtectedRoute canAccess={canCreateOrModifyContract}>
                  <Contract route="form" />
                </ProtectedRoute>
              ),
            },
            {
              path: "Edit-Contract",
              element: (
                <ProtectedRoute canAccess={canCreateOrModifyContract}>
                  <Contract route="form" />
                </ProtectedRoute>
              ),
            },
            {
              path: "View-Contract",
              element: (
                <ProtectedRoute canAccess={canViewContract}>
                  <Contract route="view" />
                </ProtectedRoute>
              ),
            },
            {
              path: "Sign-Contract",
              element: canCreateModifyReservation ? (
                <Contract route="sign" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Vehicle-Exchange",
              element: (
                <ProtectedRoute canAccess={canCreateModifyReservation}>
                  <VehicleExchangeForm />
                </ProtectedRoute>
              ),
            },
            {
              path: "Payments",
              element: (
                <ProtectedRoute canAccess={canCreateModifyReservation}>
                  <Payments />
                </ProtectedRoute>
              ),
            },
            {
              path: "Damages",
              element: (
                <ProtectedRoute canAccess={canCreateModifyReservation}>
                  <DamagesForm />
                </ProtectedRoute>
              ),
            },
            {
              path: "AddDamages",
              element: (
                <ProtectedRoute canAccess={canCreateModifyReservation}>
                  <AddDamagesForm />
                </ProtectedRoute>
              ),
            },
            {
              path: "DetectDamages",
              element: (
                <ProtectedRoute canAccess={canCreateModifyReservation}>
                  <DetectDamagesForm />
                </ProtectedRoute>
              ),
            },
          ],
        },

        {
          path: "Invoice",
          children: [
            {
              path: "",
              element: canViewInvoice ? (
                <Invoice />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Add-Invoice",
              element: canCreateInvoice ? (
                <Invoice route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Edit-Invoice",
              element: canCreateInvoice ? (
                <Invoice route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "View-Contract",
              element: canCreateInvoice ? (
                <Invoice route="view" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
          ],
        },
        {
          path: "Asset",
          children: [
            {
              path: "",
              element: canViewAsset ? (
                <Asset />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Add-Asset",
              element: canCreateAsset ? (
                <Asset route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Edit-Asset",
              element: canUpdateAsset ? (
                <Asset route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "View-Asset",
              element: canViewAsset ? (
                <Asset route="view" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
          ],
        },

        {
          path: "Customer",
          children: [
            {
              path: "",
              element: canViewCustomer ? (
                <Customer />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Add-Customer",
              element: canCreateCustomer ? (
                <Customer route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Edit-Customer",
              element: canUpdateCustomer ? (
                <Customer route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "View-Customer",
              element: canViewCustomer ? (
                <Customer route="view" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
          ],
        },

        {
          path: "Settings",
          element: <Settings />,
          children: [
            {
              path: "",
              element: <Navigate to="Location" replace={true} />,
            },
            {
              path: "Location",
              children: [
                {
                  path: "",
                  element: canViewLocation ? (
                    <SettingsLocationManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "View-Location",
                  element: canViewLocation ? (
                    <SettingsLocationManagement route="view" />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
              ],
            },

            {
              path: "Driver",
              children: [
                {
                  path: "",
                  element: canViewDriver ? (
                    <SettingsDriverManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "View-Driver",
                  element: canViewDriver ? (
                    <SettingsDriverManagement route="view" />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
              ],
            },

            {
              path: "Insurance",
              children: [
                {
                  path: "",
                  element: canViewInsurance ? (
                    <SettingsInsuranceManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "View-Insurance",
                  element: canViewInsurance ? (
                    <SettingsInsuranceManagement route="view" />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
              ],
            },

            {
              path: "User",
              children: [
                {
                  path: "",
                  element: canViewUsersGrid ? (
                    <UserManagementGrid />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "View-User",
                  element: canViewUsersGrid ? (
                    <UserView />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },

                {
                  path: "Profile",
                  element: canViewProfile ? (
                    <Profile />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
              ],
            },

            {
              path: "Rate",
              children: [
                {
                  path: "RateName",
                  element: canViewAssetRate ? (
                    <RateNameManagementGrid />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "RatePlan",
                  children: [
                    {
                      path: "",
                      element: canViewAssetRate ? (
                        <RatePlanManagementGrid />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "View-RatePlan",
                      element: canViewAssetRate ? (
                        <RatePlanView />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },
                {
                  path: "AddOnCharge",

                  children: [
                    {
                      path: "",
                      element: canViewAssetRate ? (
                        <AddOnChargesPage />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "View-AddOnCharge",
                      element: canViewAssetRate ? (
                        <AddOnView />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },
                {
                  path: "Promotion",
                  children: [
                    {
                      path: "",
                      element: canViewAssetRate ? (
                        <PromotionView />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },
                {
                  path: "EnergyCost",
                  children: [
                    {
                      path: "",
                      element: (
                        <ProtectedRoute canAccess={canViewAssetRate}>
                          <FuelPriceGrid />
                        </ProtectedRoute>
                      ),
                    },
                  ],
                },
              ],
            },

            {
              path: "Asset",
              children: [
                {
                  path: "AssetMake",
                  element: canViewAssestMake ? (
                    <SettingsVechicleMakeManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "AssetModel",
                  element: canViewAssestModel ? (
                    <SettingVehicleModelManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "AssetType",
                  element: canViewAssestType ? (
                    <SettingVehicleTypeManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "AssetOption",
                  element: canViewAssestOption ? (
                    <SettingVehicleOptionManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "MaintenanceSchedule",
                  element: (
                    <ProtectedRoute canAccess={canCreateAsset}>
                      <VehicleMaintenanceScheduleGrid />
                    </ProtectedRoute>
                  ),
                },
              ],
            },

            {
              path: "Client",
              children: [
                {
                  path: "Profile",
                  children: [
                    {
                      path: "",
                      element: canViewClientProfile ? (
                        <ClientView />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "Edit-Profile",
                      element: canCreateAndModifylientProfile ? (
                        <CilentEditView />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },
                {
                  path: "Tax",
                  children: [
                    {
                      path: "",
                      element: canViewTax ? (
                        <TaxManagementGrid />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },
                {
                  path: "TaxMapping",
                  children: [
                    {
                      path: "",
                      element: canViewTax ? (
                        <TaxMappingPage />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },

                {
                  path: "ChangePassword",
                  children: [
                    {
                      path: "",
                      element: canUpdateUserSelf ? (
                        <ChangePassword />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },
                {
                  path: "store",
                  element: (
                    <ProtectedRoute canAccess={canViewContract}>
                      <Store />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: "OffenseType",
                  element: (
                    <ProtectedRoute canAccess={canViewContract}>
                      <OffenseTypeGrid />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: "Referral",
                  children: [
                    {
                      path: "",
                      element: (
                        <ProtectedRoute canAccess={canViewContract}>
                          <ReferralGrid />
                        </ProtectedRoute>
                      ),
                    },
                  ],
                },
                {
                  path: "Vendor",
                  children: [
                    {
                      path: "",
                      element: (
                        <ProtectedRoute canAccess={canViewVendor}>
                          <VendorGrid />
                        </ProtectedRoute>
                      ),
                    },
                  ],
                },
                {
                  path: "Expense",
                  children: [
                    {
                      path: "",
                      element: (
                        <ProtectedRoute canAccess={canViewExpense}>
                          <ExpenseGrid />
                        </ProtectedRoute>
                      ),
                    },
                  ],
                },
                {
                  path: "TermsAndCondition",
                  children: [
                    {
                      path: "",
                      element: (
                        <ProtectedRoute canAccess={canViewTerms}>
                          <TermsAndConditionGrid />
                        </ProtectedRoute>
                      ),
                    },
                  ],
                },
                {
                  path: "Source",
                  children: [
                    {
                      path: "",
                      element: (
                        <ProtectedRoute canAccess={canCreateModifyReservation}>
                          <SourceGrid />
                        </ProtectedRoute>
                      ),
                    },
                  ],
                },
              ],
            },
          ],
        },

        {
          path: "Reports",
          element: <ReportsPage />,
          children: [
            {
              path: "View/:id",
              element: <Report />,
            },
          ],
        },

        {
          path: "Planner",
          element: canViewPlanner ? (
            <PlannerComponent />
          ) : (
            <Navigate to="/Access-Denied" replace={true} />
          ),
        },
        {
          path: "CallAgent",
          element: (
            <ProtectedRoute canAccess={canViewReservation}>
              <CallAgent />
            </ProtectedRoute>
          ),
        },
        {
          path: "EmailConversionBookingLog",
          element: (
            <ProtectedRoute canAccess={canViewReservation}>
              <EmailLoader />
            </ProtectedRoute>
          ),
        },
      ],
    },
    {
      path: "Access-Denied",
      element: <>Access Denied!</>,
    },
    {
      path: "Sign-Contract",
      element: canCreateModifyReservation ? (
        <AuthGuard>
          <Suspense fallback={<CustomLoader />}>
            <ContractSigned />
          </Suspense>
        </AuthGuard>
      ) : (
        <Navigate to="/Access-Denied" replace={true} />
      ),
    },
    {
      path: "login",
      element: (
        <GuestGuard>
          <Authentication />
        </GuestGuard>
      ),
    },
    {
      path: "*",
      element: <CustomLoader />,
    },
  ]);

  return routes;
};
export default Routes;
